import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  TokenValidationResponse,
  UserInfoResponse,
} from '../types/auth';
import axiosAdmin from './instance/axiosAdmin';

// 로그인 API
export const loginAPI = async (
  loginData: LoginRequest
): Promise<LoginResponse> => {
  try {
    const response = await axiosAdmin.post<LoginResponse>(
      '/auth/login',
      loginData
    );

    // 백엔드 표준 응답 형식 검증
    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '로그인에 실패했습니다.');
    }

    // 필수 데이터 검증
    if (
      !response.data.data ||
      !response.data.data.accessToken ||
      !response.data.data.user
    ) {
      throw new Error('로그인 응답에서 필수 데이터를 찾을 수 없습니다.');
    }

    return response.data;
  } catch (error: any) {
    // API 에러 처리
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 사용자 정보 조회 API
export const getUserInfoAPI = async (): Promise<UserInfoResponse> => {
  try {
    const response = await axiosAdmin.get<UserInfoResponse>('/auth/me');

    // 백엔드 표준 응답 형식 검증
    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '사용자 정보 조회에 실패했습니다.'
      );
    }

    return response.data;
  } catch (error: any) {
    // API 에러 처리
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 권한 정보 조회 API
export const getUserPermissionsAPI = async (): Promise<any> => {
  try {
    const response = await axiosAdmin.get<ApiResponse<any>>(
      '/auth/me/permissions'
    );

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '권한 정보 조회에 실패했습니다.'
      );
    }

    console.log('[getUserPermissionsAPI] 원본 응답:', response.data.data);
    return response.data.data;
  } catch (error: any) {
    // API 에러 처리
    console.error('[getUserPermissionsAPI] API 에러:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 초대 토큰 검증 API
export const validateInviteTokenAPI = async (
  token: string
): Promise<TokenValidationResponse> => {
  try {
    const response = await axiosAdmin.get<ApiResponse<TokenValidationResponse>>(
      `/auth/validate-invite-token?token=${encodeURIComponent(token)}`
    );

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '토큰 검증에 실패했습니다.');
    }

    if (!response.data.data) {
      throw new Error('토큰 검증 데이터를` 찾을 수 없습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 회원가입 API
export const registerUserAPI = async (data: RegisterRequest): Promise<void> => {
  try {
    const response = await axiosAdmin.post<ApiResponse<void>>(
      '/auth/register',
      data
    );

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '회원가입에 실패했습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 관리자 권한별 메뉴 조회
export const getAdminMenuAPI = async (): Promise<{
  menuItems: {
    id: string;
    title: string;
    icon: string;
    path: string;
    type: 'admin' | 'partner';
  }[];
  userType: string;
  partnerName?: string;
}> => {
  try {
    const response = await axiosAdmin.get<
      ApiResponse<{
        menuItems: {
          id: string;
          title: string;
          icon: string;
          path: string;
          type: 'admin' | 'partner';
        }[];
        userType: string;
        partnerName?: string;
      }>
    >('/auth/menu');

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '메뉴 조회에 실패했습니다.');
    }

    if (!response.data.data) {
      throw new Error('메뉴 데이터를 찾을 수 없습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    console.log('[getAdminMenuAPI] error', error);
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 파트너사 권한별 메뉴 조회
export const getPartnerMenuAPI = async (): Promise<{
  menuItems: {
    id: string;
    title: string;
    icon: string;
    path: string;
    type: 'admin' | 'partner';
  }[];
  userType: string;
  partnerName?: string;
}> => {
  try {
    const response = await axiosAdmin.get<
      ApiResponse<{
        menuItems: {
          id: string;
          title: string;
          icon: string;
          path: string;
          type: 'admin' | 'partner';
        }[];
        userType: string;
        partnerName?: string;
      }>
    >('/partner-admin/menu');

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '파트너 메뉴 조회에 실패했습니다.'
      );
    }

    if (!response.data.data) {
      throw new Error('파트너 메뉴 데이터를 찾을 수 없습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 현재 사용자의 최신 권한 정보 조회 (토큰 갱신 없이)
export const checkUserPermissionsAPI = async (): Promise<{
  permissions: any[];
  roles: string[];
  lastUpdated: string;
}> => {
  try {
    const response = await axiosAdmin.get<
      ApiResponse<{
        permissions: any[];
        roles: string[];
        lastUpdated: string;
      }>
    >('/auth/permissions-check');

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '권한 확인에 실패했습니다.');
    }

    if (!response.data.data) {
      throw new Error('권한 데이터를 찾을 수 없습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 권한 변경 확인 API
export const checkPermissionChangeAPI = async (): Promise<{
  hasChange: boolean;
  notification: {
    userId: string;
    email: string;
    changeType: 'role_update' | 'permission_update' | 'access_revoked';
    timestamp: string;
    newPermissions?: string[];
    oldPermissions?: string[];
    changedBy?: string;
  } | null;
}> => {
  try {
    const response = await axiosAdmin.get<
      ApiResponse<{
        hasChange: boolean;
        notification: any;
      }>
    >('/auth/permission-change-check');

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '권한 변경 확인에 실패했습니다.'
      );
    }

    if (!response.data.data) {
      throw new Error('권한 변경 데이터를 찾을 수 없습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 권한 변경 알림 확인 완료 API
export const acknowledgePermissionChangeAPI = async (): Promise<void> => {
  try {
    const response = await axiosAdmin.post<ApiResponse<void>>(
      '/auth/permission-change-acknowledge'
    );

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '권한 변경 알림 확인에 실패했습니다.'
      );
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

export const getAllUsersAPI = async (): Promise<{
  users: any[];
}> => {
  try {
    const response =
      await axiosAdmin.get<ApiResponse<any>>('/permission/users');

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '사용자 목록 조회에 실패했습니다.'
      );
    }

    // API 응답이 직접 배열이면 users 속성으로 래핑, 이미 { users: [] } 형태면 그대로 사용
    if (Array.isArray(response.data.data)) {
      return { users: response.data.data };
    } else if (response.data.data && Array.isArray(response.data.data.users)) {
      return response.data.data;
    } else {
      return { users: [] };
    }
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 디버깅용 사용자 권한 조회 API
export const debugUserPermissionsAPI = async (email: string): Promise<any> => {
  try {
    const response = await axiosAdmin.get<ApiResponse<any>>(
      `/auth/debug/user-permissions/${encodeURIComponent(email)}`
    );

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(
        response.data.message || '디버깅 정보 조회에 실패했습니다.'
      );
    }

    return response.data.data;
  } catch (error: any) {
    console.error('[debugUserPermissionsAPI] API 에러:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 사용자 삭제 API
export const deleteUserAPI = async (
  userId: string
): Promise<{
  deletedUser: any;
  deletedCount: number;
}> => {
  try {
    const response = await axiosAdmin.post<
      ApiResponse<{
        deletedUser: any;
        deletedCount: number;
      }>
    >(`/auth/users/${userId}/delete`);

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '사용자 삭제에 실패했습니다.');
    }

    if (!response.data.data) {
      throw new Error('삭제 결과 데이터를 찾을 수 없습니다.');
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || '네트워크 오류가 발생했습니다.');
  }
};

// 로그아웃 API
export const logoutAPI = async (): Promise<void> => {
  try {
    const response = await axiosAdmin.post<ApiResponse<void>>('/auth/logout');

    if (!response.data.success || response.data.code !== 200) {
      throw new Error(response.data.message || '로그아웃에 실패했습니다.');
    }
  } catch (error: any) {
    console.error('[logoutAPI] API 에러:', error);
    // 로그아웃은 실패하더라도 클라이언트에서 토큰을 제거해야 함
    if (error.response?.data?.message) {
      console.warn('로그아웃 API 실패:', error.response.data.message);
    } else {
      console.warn('로그아웃 API 실패:', error.message);
    }
  }
};
